import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { hasVisibleContent } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

export const documentContentStatePluginKey: PluginKey = new PluginKey('ai-document-content-state');

export type DocumentContentState = {
	consumerEmptyStateOverride?: boolean;
	hasVisibleContent: boolean;
};

export const SET_CONSUMER_EMPTY_STATE_OVERRIDE = 'set-consumer-empty-state-override';

export const createDocumentContentStatePlugin = (
	initialConsumerEmptyStateOverride?: boolean,
): SafePlugin<DocumentContentState> => {
	const isSmartRepliesEnabled = UNSAFE_expValNoExposure(
		'jira-smart-replies-v2',
		'isEnabled',
		false,
	);
	return new SafePlugin<DocumentContentState>({
		key: documentContentStatePluginKey,
		state: {
			init(_, state) {
				return {
					hasVisibleContent: hasVisibleContent(state.doc),
					consumerEmptyStateOverride: isSmartRepliesEnabled
						? initialConsumerEmptyStateOverride
						: undefined,
				};
			},
			apply(tr, pluginState, _, newState) {
				if (isSmartRepliesEnabled) {
					let next = pluginState;
					const meta = tr.getMeta(documentContentStatePluginKey);
					if (
						meta?.type === SET_CONSUMER_EMPTY_STATE_OVERRIDE &&
						meta?.value !== next.consumerEmptyStateOverride
					) {
						next = { ...next, consumerEmptyStateOverride: meta.value };
					}

					if (tr.docChanged) {
						const newHasVisibleContent = hasVisibleContent(newState.doc);
						if (next.hasVisibleContent !== newHasVisibleContent) {
							next = { ...next, hasVisibleContent: newHasVisibleContent };
						}
					}
					return next;
				}

				if (!tr.docChanged) {
					return pluginState;
				}

				const newHasVisibleContent = hasVisibleContent(newState.doc);
				if (pluginState.hasVisibleContent !== newHasVisibleContent) {
					return { hasVisibleContent: newHasVisibleContent };
				}

				return pluginState;
			},
		},
	});
};
